import type { GenericFormType } from '@neo/blocks/forms/types'
import { Badge } from '@neo/index'
import { Input } from '@neo/primitives/input'
import { Label } from '@neo/primitives/label'
import { Select } from '@neo/primitives/select'
import { Tooltip } from '@neo/primitives/tooltip'
import { Info, Settings2, WandSparkles } from 'lucide-react'
import { NodevarList } from 'v2/pages/flow/components/nodevar-list'
import { useActionFieldType } from 'v2/pages/flow/hooks/use-action-fields'
import type { ActionFieldType, AiActionNodeData } from 'v2/pages/flow/types'

interface ActionFieldProps {
  fieldConfig: {
    name: string
    type?: string
    description?: string
    isOptional: boolean
    default?: unknown
  }
  formContext: {
    form: GenericFormType<AiActionNodeData['config']>
    arrayIndex: number
    allNodeVars: string[]
  }
}

export const ActionField = ({ fieldConfig, formContext }: ActionFieldProps) => {
  const {
    name: field,
    type: fieldDataType = 'string',
    description,
    isOptional,
    default: defaultValue,
  } = fieldConfig
  const { form, arrayIndex, allNodeVars } = formContext

  // Use the shared hook for field type management
  const { getFieldType, setFieldType } = useActionFieldType({ form, arrayIndex })

  const fieldType = getFieldType(field, isOptional)
  const inputValue = form.getFieldValue(`actions[${arrayIndex}].action_inputs.${field}`) || ''

  const HorizontalSeparator = () => (
    <div
      className="absolute left-0 top-4 w-full h-0.5 border-t-2 border-dotted border-st1"
      style={{ width: '1.3rem', left: '-1.3rem' }}
      aria-hidden="true"
    />
  )

  // Render field input based on type
  const renderInput = () => {
    const testId = `${isOptional ? 'optional-action-input-field' : 'default-action-input-field'}-${field}`

    const getPlaceholder = () => {
      if (defaultValue !== undefined) {
        let defaultAsString = ''
        if (typeof defaultValue === 'string') defaultAsString = defaultValue
        else if (typeof defaultValue === 'boolean' || typeof defaultValue === 'number')
          defaultAsString = defaultValue.toString()
        else defaultAsString = String(defaultValue)
        return defaultAsString ? `Default: ${defaultAsString}` : 'Select or enter a value'
      }
      return 'Select or enter a value'
    }

    if (fieldDataType === 'boolean') {
      // Create static boolean values for NodevarList (without {{ }} wrapper, like sender IDs)
      const booleanNodeVars = ['true', 'false', ...allNodeVars]

      return (
        <Input
          value={inputValue}
          onChange={(e) => {
            form.setFieldValue(`actions[${arrayIndex}].action_inputs.${field}`, e.target.value)
            form.validateField(`actions[${arrayIndex}].action_inputs.${field}`, 'change')
          }}
          placeholder={getPlaceholder()}
          endIcon={
            fieldType === 'manual' ? (
              <NodevarList
                nodevars={booleanNodeVars}
                className="pr-2"
                inputElementSelector={`input[data-testid="${testId}"]`}
                replaceEntireField
              />
            ) : null
          }
          data-testid={testId}
        />
      )
    }

    // Default to string input
    return (
      <Input
        value={inputValue}
        onChange={(e) => {
          form.setFieldValue(`actions[${arrayIndex}].action_inputs.${field}`, e.target.value)
          form.validateField(`actions[${arrayIndex}].action_inputs.${field}`, 'change')
        }}
        placeholder={getPlaceholder()}
        endIcon={
          fieldType === 'manual' ? (
            <NodevarList
              nodevars={allNodeVars}
              className="pr-2"
              inputElementSelector={`input[data-testid="${testId}"]`}
              replaceEntireField
            />
          ) : null
        }
        data-testid={testId}
      />
    )
  }

  return (
    <div className="relative ml-0 mt-[10px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 mb-1">
          <Label className="block text-s-500">
            {field}{' '}
            {isOptional && <span className="text-xs-400 text-tertiary italic">(optional)</span>}
          </Label>
          {description && (
            <Tooltip content={description}>
              <Info className="h-3 w-3 text-tertiary cursor-pointer mt-0.5" />
            </Tooltip>
          )}
        </div>
        <Select
          variant="ghost"
          size="sm"
          className="w-fit form-button-ghost"
          searchable={false}
          options={[
            {
              label: (
                <span className="flex items-center gap-1 text-status-progress text-xs-500">
                  <WandSparkles className="h-3 w-3" />
                  Auto
                </span>
              ),
              value: 'auto',
              searchValue: 'Auto',
            },
            {
              label: (
                <span className="flex items-center gap-1 text-status-warning text-xs-500">
                  <Settings2 className="h-3 w-3" />
                  Manual
                </span>
              ),
              value: 'manual',
              searchValue: 'Manual',
            },
          ]}
          value={fieldType}
          onChange={(value) => {
            setFieldType(field, value as ActionFieldType, isOptional)
          }}
        />
      </div>
      {fieldType === 'auto' ? (
        <div className="relative">
          <HorizontalSeparator />
          <div className="border bg-bg3 rounded-md px-2 py-1 form-button">
            <Badge variant="progress" className="text-xs-500">
              Autofill with AI
            </Badge>
          </div>
        </div>
      ) : (
        <div className="relative">
          <HorizontalSeparator />
          <div className="flex flex-col gap-1">{renderInput()}</div>
        </div>
      )}
    </div>
  )
}
